/**
 * @file CompanySessionsPage.tsx
 * @brief Company Admin → Sessions (super-admin parity, company scope).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — GridBlock end-to-end (match Users/Companies)
 */

import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { Avatar } from "@/components/ui/Avatar";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { SessionKindBadge } from "@/features/sessions/components/SessionKindBadge";
import { SessionRowActions } from "@/features/sessions/components/SessionRowActions";
import { canDeleteOwnSession } from "@/features/sessions/sessionOwnership";
import { useAuth } from "@/shared/auth/auth-context";
import { SessionStatusBadge } from "@/features/sessions/components/SessionStatusBadge";
import {
  formatDateTime,
  formatDurationSeconds,
  formatFullName,
  formatLanguagePair,
  formatMoneyMinor,
  getInitials,
} from "@/shared/utils/formatters";
import {
  companySessionsPagination,
  ownerGroupKey,
  useCompanySessionsEmployees,
  useCompanySessionsGroupRows,
  useCompanySessionsList,
} from "./api";
import {
  sessionEmployeeLabel,
  type CompanySessionRow,
  type CompanySessionsListParams,
} from "./schemas";
import {
  CompanySessionsFilters,
  CompanySessionsHeaderFilters,
} from "./components/CompanySessionsFilters";

function paramsFromSearch(search: URLSearchParams): CompanySessionsListParams {
  const status = search.get("status");
  const kind = search.get("kind");
  return {
    page: Number(search.get("page")) || 1,
    limit: Number(search.get("limit")) || 25,
    q: search.get("q") || undefined,
    from: search.get("from") || undefined,
    to: search.get("to") || undefined,
    employee_id: search.get("employee_id") || undefined,
    status:
      status === "in_progress" ||
      status === "completed" ||
      status === "failed" ||
      status === "cancelled"
        ? status
        : undefined,
    kind: kind === "standard" || kind === "realtime" ? kind : undefined,
  };
}

function searchFromParams(p: CompanySessionsListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.limit && p.limit !== 25) usp.set("limit", String(p.limit));
  if (p.q) usp.set("q", p.q);
  if (p.from) usp.set("from", p.from);
  if (p.to) usp.set("to", p.to);
  if (p.employee_id) usp.set("employee_id", p.employee_id);
  if (p.status) usp.set("status", p.status);
  if (p.kind) usp.set("kind", p.kind);
  return usp;
}

/**
 * `self`: a user's own sessions at `/app/sessions` (DES 01-10-2026: "a
 * normal user cant see his sessions"). The backend already scopes
 * `GET /api/sessions` to the caller for a non-admin; this hides the
 * employee filter and the per-employee grouping. Delete shows only for a
 * personal account on a session it owns (DES 21-10-2026, sessionOwnership).
 */
export function CompanySessionsPage({ self = false }: { self?: boolean } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);
  /**
   * A session opens as its own page (DES 01-10-2026: "make this a page like
   * meeting preview"). `?session=<id>` still works for older links (Medical
   * Assessment used it, 14-09-2026): it forwards to the page.
   */
  const navigate = useNavigate();
  const { user } = useAuth();
  const legacySession = searchParams.get("session");
  const base = self ? "/app/sessions" : "/company/sessions";
  const openSession = (id: string) => {
    try {
      sessionStorage.setItem(
        // Read at click time: the row menu's handler is memoised.
        `company-sessions-scroll:${new URLSearchParams(window.location.search).toString()}`,
        String(scrollBox()?.scrollTop ?? 0),
      );
    } catch {
      /* storage blocked: Back just starts at the top */
    }
    navigate(`${base}/${encodeURIComponent(id)}`);
  };

  const isGrouped = !self && !params.employee_id;

  /**
   * Grouped (company admin, no employee picked): every employee with a
   * session, from the server, not the employees who happen to be on page 1
   * (DES 01-10-2026: "it should always show all users in the list
   * regardless of the page limitation"). A group fetches its own sessions
   * when opened, 25 at a time. Zero-session employees are not listed (DES).
   */
  const list = useCompanySessionsList(params, { enabled: !isGrouped });
  const employees = useCompanySessionsEmployees(params, isGrouped);
  /**
   * Open groups live in the URL (`open=key:batches,…`, replace, so Back
   * restores them): opening a session and coming back had rebuilt the page
   * with every group shut (DES 01-10-2026: "wapis jata hoon to pechay wala
   * page reload ho kar poora band ho jata hai").
   */
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, as the table did, so matches are visible.
  const searching = isGrouped && Boolean(params.q?.trim());
  const openList = useMemo(() => {
    const keys = searching
      ? (employees.data ?? []).map((e) => ownerGroupKey(e.owner_id))
      : [...openGroups.keys()];
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, employees.data, openGroups]);
  const groupRows = useCompanySessionsGroupRows(params, openList);

  const groups = useMemo<TableRowGroup<CompanySessionRow>[] | undefined>(() => {
    if (!isGrouped || !employees.data?.length) return undefined;
    return employees.data.map((e) => {
      const key = ownerGroupKey(e.owner_id);
      const idx = openList.findIndex((o) => o.key === key);
      const q = idx >= 0 ? groupRows[idx] : undefined;
      const name = formatFullName(e.first_name, e.last_name);
      return {
        key,
        label: name !== "—" ? name : e.email ?? e.person_a_name ?? "Unknown",
        avatarSrc: e.avatar_url,
        avatarGender: e.gender,
        avatarInitials: getInitials(e.first_name, e.last_name, e.email),
        rows: q?.data?.sessions ?? [],
        total: e.count,
        loading: Boolean(q?.isFetching),
        error: Boolean(q?.isError),
        onRetry: () => void q?.refetch(),
      };
    });
  }, [isGrouped, employees.data, openList, groupRows]);
  const rows = isGrouped
    ? (groups ?? []).flatMap((g) => g.rows)
    : list.data?.sessions ?? [];

  // Scroll: saved when a session opens, put back once the open groups have
  // their rows again (the shell scrolls an inner box and does not restore).
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The box that really scrolls vertically: a table wrapper with only
  // overflow-x also computes overflow-y "auto", so it must also be taller
  // inside than out. Falls back to the document.
  const scrollBox = (): Element | null => {
    let el = rootRef.current?.parentElement ?? null;
    while (el) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight + 1) {
        return el;
      }
      el = el.parentElement;
    }
    return document.scrollingElement;
  };
  const scrollKey = `company-sessions-scroll:${searchParams.toString()}`;
  const restored = useRef(false);
  // The phone's tips icon (DES 05-10-2026), in web words.
  const tips = useHintPopover("sessions_list_tips");
  const groupsLoading = groupRows.some((q) => q.isFetching && !q.data);
  useEffect(() => {
    if (restored.current || employees.isLoading || list.isLoading || groupsLoading) return;
    restored.current = true;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(scrollKey);
      sessionStorage.removeItem(scrollKey);
    } catch {
      /* storage blocked: nothing to restore */
    }
    const y = Number(saved);
    if (saved && y > 0) requestAnimationFrame(() => scrollBox()?.scrollTo({ top: y }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees.isLoading, list.isLoading, groupsLoading]);
  const paginationMeta = companySessionsPagination(list.data, params);
  const pagination: PaginationState | undefined = !isGrouped && list.data
    ? {
        page: paginationMeta.page,
        limit: paginationMeta.limit,
        total: paginationMeta.total,
        totalPages: paginationMeta.totalPages,
      }
    : undefined;

  const setParams = (next: CompanySessionsListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  const columns = useMemo<ColumnDef<CompanySessionRow>[]>(
    () => [
      {
        id: "session",
        // Over the indented session ids when grouped (DES 01-10-2026).
        header: () => <span className={isGrouped ? "pl-7" : undefined}>Session</span>,
        cell: ({ row }) => {
          const s = row.original;
          if (isGrouped) {
            return (
              <p className="truncate pl-7 font-mono text-xs text-foreground">
                {s.session_id}
              </p>
            );
          }
          const label = sessionEmployeeLabel(s);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={s.person_a_avatar_url}
                gender={s.person_a_gender}
                initials={getInitials(
                  s.owner_first_name ?? s.participant_first_name,
                  s.owner_last_name ?? s.participant_last_name,
                  s.owner_email ?? s.participant_email,
                )}
                alt=""
                size="md"
                tone="brand"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {formatFullName(
                    s.owner_first_name ?? s.participant_first_name,
                    s.owner_last_name ?? s.participant_last_name,
                  ) !== "—"
                    ? formatFullName(
                        s.owner_first_name ?? s.participant_first_name,
                        s.owner_last_name ?? s.participant_last_name,
                      )
                    : label}
                </p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {s.session_id}
                </p>
              </div>
            </div>
          );
        },
      },
      {
        id: "kind",
        header: "Kind",
        cell: ({ row }) => (
          <SessionKindBadge kind={row.original.session_kind} />
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <SessionStatusBadge status={row.original.status} />
        ),
      },
      {
        id: "languages",
        header: "Languages",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatLanguagePair(
              row.original.source_language ?? "",
              row.original.target_language ?? "",
            )}
          </span>
        ),
      },
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatDurationSeconds(Number(row.original.duration_seconds ?? 0))}
          </span>
        ),
      },
      {
        id: "cost",
        header: "Cost",
        cell: ({ row }) => (
          <span className="text-sm font-medium tabular-nums">
            {formatMoneyMinor(
              Number(row.original.total_cents ?? 0),
              row.original.currency ?? "GBP",
            )}
          </span>
        ),
      },
      {
        id: "started",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(row.original.started_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) =>
          self && !canDeleteOwnSession(user, row.original) ? null : (
            <div className="flex items-center justify-end">
              <SessionRowActions
                scope="company"
                sessionId={row.original.session_id}
                onView={(id) => openSession(id)}
              />
            </div>
          ),
      },
    ],
    [isGrouped, self, user],
  );

  if (legacySession) {
    return <Navigate to={`${base}/${encodeURIComponent(legacySession)}`} replace />;
  }

  return (
    <div ref={rootRef} className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">
              {self ? "My sessions" : "Sessions"}
            </h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {self
                ? "Your interpretation sessions. Start one in the Verblizr app with Slide to Interpret; it shows here when it finishes. Click a row to open it."
                : "Browse, filter, and inspect interpretation sessions for your company. Click any row to open utterances + transcript."}
            </p>
          </div>
          {/* The filters, top right, as on Notes and Boardrooms (DES 01-10-2026):
              text pills, then the date circle. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <CompanySessionsHeaderFilters value={params} onChange={setParams} hideEmployee={self} />
            <DateRangePicker
              variant="circle"
              from={params.from}
              to={params.to}
              onChange={({ from, to }) => setParams({ ...params, from, to, page: 1 })}
            />
            <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} />
          </div>
          {tips.open ? (
            <HintBubble
              lines={[
                "Click a session to read and replay the conversation.",
                ...(isGrouped ? ["Click a person's name to show or hide their sessions."] : []),
                ...(self
                  ? []
                  : ["Use ⋯ on a session to delete its audio, transcript or all its data."]),
              ]}
              onClose={tips.close}
              anchorRef={tips.anchorRef}
              className="basis-full"
            />
          ) : null}
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable<CompanySessionRow, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            groupItemLabel="sessions"
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            isLoading={isGrouped ? employees.isLoading : list.isLoading}
            isFetching={isGrouped ? employees.isFetching : list.isFetching}
            error={isGrouped ? employees.error : list.error}
            onRetry={() => (isGrouped ? employees.refetch() : list.refetch())}
            rowKey={(s) => s.session_id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(limit) =>
              setParams({ ...params, limit, page: 1 })
            }
            onRowClick={(s) => openSession(s.session_id)}
            emptyShape="session"
            emptyTitle="No sessions match those filters"
            emptyDescription="Adjust the filters or wait for fresh sessions from your team."
          />
        </GridCell>
      </GridBlock>

      {/* The Notes screen's search (DES 01-10-2026), docked at the bottom
          like Notes: "dock style ki tarha neechay hoon". */}
      <PageDock>
        <CompanySessionsFilters value={params} onChange={setParams} />
      </PageDock>
    </div>
  );
}
